import { clearSupabaseCookies } from "@/lib/utils/auth/clear-cookies";
import { logger } from "@/lib/logger";
import { routeAfterSignIn } from "./route-after-sign-in";
import type { AppRouter, SupabaseBrowserClient } from "./types";

interface CreatePasskeySignInHandlerArgs {
  supabase: SupabaseBrowserClient;
  router: AppRouter;
  redirectAfterLogin?: string | null;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

export function createPasskeySignInHandler(args: CreatePasskeySignInHandlerArgs) {
  const { supabase, router, redirectAfterLogin, setIsLoading, setError, setSuccess } = args;

  return async (userType: "employee" | "client") => {
    // WebAuthn requires a user gesture + a secure context (HTTPS or localhost).
    if (typeof window !== "undefined" && !window.PublicKeyCredential) {
      setError("This device or browser doesn't support passkeys.");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Start from a clean cookie state, matching the other sign-in flows.
      const clearResult = clearSupabaseCookies();
      logger.info("Cleared cookies before passkey sign-in:", { clearResult });

      // Discoverable-credential sign-in: the authenticator resolves the account,
      // so no email is required up front. Handles the full WebAuthn ceremony.
      const { data, error } = await supabase.auth.signInWithPasskey();

      if (error) {
        logger.error("Passkey sign-in error", error);
        // The user cancelling the native prompt surfaces as an abort/NotAllowed —
        // don't treat that as a hard error.
        const message = error.message || "";
        if (/abort|notallowed|cancel/i.test(message)) {
          setError("Passkey sign-in was cancelled.");
        } else {
          setError(message || "Passkey sign-in failed. Please try again.");
        }
        return;
      }

      if (!data.user) {
        setError("Passkey sign-in did not return a session. Please try again.");
        return;
      }

      await routeAfterSignIn({
        supabase,
        router,
        user: data.user,
        userType,
        redirectAfterLogin,
        setError,
      });
    } catch (err) {
      logger.error("Unexpected passkey sign-in error", err);
      setError("An unexpected error occurred during passkey sign-in");
    } finally {
      setIsLoading(false);
    }
  };
}
